import { TFunction } from "i18next";

import i18n from "../i18n";
import { Layout } from "../model/app/propTypes";
import { LayoutMetadata } from "../types/api-schema";

import {
  MAPSET_LAYOUT_DESCRIPTION,
  MAPSET_LAYOUT_FILENAME,
  MAPSET_LAYOUT_MAP,
  MAPSET_LAYOUT_TITLE,
} from "./constants";

import type { AppDomainConfig } from "../envConfig";

export function getLayoutVariableValue(
  value:
    | ((_t: TFunction, _altString?: string) => string)
    | null
    | string
    | undefined,
  t: TFunction,
  altString?: string,
): string | undefined {
  if (typeof value === "function") {
    return value(t, altString);
  }
  return value || altString;
}

function getLocalizedLayoutValue(values: Record<string, string> | undefined) {
  for (const language of i18n.languages || [i18n.language]) {
    const value = values?.[language] ?? values?.[language.split("-")[0]];
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

function processLayouts(
  layoutsJson: LayoutMetadata[],
  config?: AppDomainConfig,
): Layout[] {
  return (layoutsJson || []).map((layout) => {
    // Keep in sync with _DEFAULT_MAP_WIDTH/_DEFAULT_MAP_HEIGHT in
    // backend/mapset/meta/layouts.py.
    const mapWidth = layout.mapWidth || 595;
    const mapHeight = layout.mapHeight || 549;
    const mapRatio = mapWidth / mapHeight;
    const layoutConfig = config?.layoutConfigs?.[layout.name] || {};

    return {
      name: layout.name,
      org: layout.org,
      properties: {
        descriptionLabels: layout.descriptionLabels,
        descriptionValues: layout.descriptionValues,
        formats: ["application/pdf"],
        logo: true,
        map: true,
        mapBleed: layout.mapBleed,
        mapHeight,
        mapQrCode: layout.mapQrCode,
        mapRatio,
        mapWidth,
        name: layout.names,
        orientation: layout.orientation || "portrait",
        qrCode: layout.qrCode,
        scales: [3],
        thumbnail: layoutConfig.thumbnail,
        titleLabels: layout.titleLabels,
      },
      uri: layout.uri,
      variables: {
        [MAPSET_LAYOUT_DESCRIPTION]: {
          defaultValue: (t: TFunction) => {
            return (
              getLocalizedLayoutValue(layout.descriptionValues) ??
              t("mapset_layout_description_placeholder")
            );
          },
          name: (t: TFunction) => {
            return (
              getLocalizedLayoutValue(layout.descriptionLabels) ??
              t("mapset_layout_description")
            );
          },
          placeholder: (t: TFunction) => {
            return t("mapset_layout_description_placeholder");
          },
          position: 2,
          type: "html",
        },
        [MAPSET_LAYOUT_FILENAME]: {
          defaultValue: (t: TFunction, planName) => {
            return planName;
          },
          type: "filename",
        },
        [MAPSET_LAYOUT_MAP]: {
          type: "screenshoturl",
        },
        [MAPSET_LAYOUT_TITLE]: {
          defaultValue: (t: TFunction, planName: string) => {
            return planName;
          },
          name: (t: TFunction) => {
            return (
              getLocalizedLayoutValue(layout.titleLabels) ??
              t("mapset_layout_title")
            );
          },
          placeholder: (t: TFunction) => {
            return getLocalizedLayoutValue(layout.titleLabels)
              ? ""
              : t("mapset_layout_title_placeholder");
          },
          position: 1,
          type: "text",
        },
        ...layoutConfig,
      },
    };
  });
}

export async function fetchAndProcessLayouts(
  url: string,
  options: RequestInit,
  config?: AppDomainConfig,
) {
  if (!url) {
    return [];
  }

  const response = await fetch(url, options).catch((error) => {
    if (error.name === "AbortError") {
      return Promise.resolve();
    }
    throw error;
  });
  if (!response || !response.ok) {
    return [];
  }
  const layoutsJson = await response.json();
  return processLayouts(layoutsJson, config);
}

export default processLayouts;
